import React, { useState, useEffect, useCallback } from 'react';
import { XMarkIcon, CalendarIcon, ClockIcon, MapPinIcon, SwatchIcon, UserPlusIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { useSelector, useDispatch } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { FaClock, FaMapMarkerAlt, FaShareAlt, FaCheck, FaUserFriends, FaUsers } from 'react-icons/fa';
import { addMinutes, format, parseISO, differenceInMinutes } from 'date-fns';
import { fetchUserConnections } from '../../../state/connectionSlice';
import calendarService from '../../../services/calendarService';
import { connectionsActions } from '../../../state/connectionSlice';

export default function NewEventForm({ 
  isOpen, 
  onClose, 
  onEventAdded, 
  initialEvent = null, 
  isEditMode = false 
}) {
  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startDateTime, setStartDateTime] = useState('');
  const [duration, setDuration] = useState({ hours: 1, minutes: 0 });
  const [location, setLocation] = useState('');
  const [color, setColor] = useState('#3B82F6');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const token = useSelector((state) => state.auth.token);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [sharedWith, setSharedWith] = useState([]);
  const connections = useSelector(state => state.connections.connections);
  const [connectionType, setConnectionType] = useState('family');
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Fetch user connections when the form is opened
  useEffect(() => {
    if (isOpen && user?.id) {
      setIsLoading(true);
      // Fetch all connection types
      dispatch(fetchUserConnections({ userId: user.id, type: 'all' }))
        .then(() => setIsLoading(false))
        .catch(() => setIsLoading(false));
    }
  }, [isOpen, user?.id, dispatch]);

  // Initialize form with initialEvent data if in edit mode
  useEffect(() => {
    if (isOpen && initialEvent && isEditMode) {
      // Set form fields from the event being edited
      setTitle(initialEvent.title || '');
      setDescription(initialEvent.description || '');
      
      // Handle date/time
      if (initialEvent.startTime || initialEvent.start) {
        const startTime = initialEvent.startTime || initialEvent.start;
        setStartDateTime(startTime ? format(parseISO(startTime), "yyyy-MM-dd'T'HH:mm") : '');
        
        // Calculate duration
        if ((initialEvent.endTime || initialEvent.end) && startTime) {
          const endTime = initialEvent.endTime || initialEvent.end;
          const durationInMinutes = differenceInMinutes(parseISO(endTime), parseISO(startTime));
          setDuration({
            hours: Math.floor(durationInMinutes / 60),
            minutes: durationInMinutes % 60
          });
        }
      }
      
      setLocation(initialEvent.location || '');
      setColor(initialEvent.color || '#3B82F6');
      
      // Handle shared users
      if (initialEvent.sharedWith && Array.isArray(initialEvent.sharedWith)) {
        const sharedUserIds = initialEvent.sharedWith.map(shared => {
          // Handle different formats of sharedWith entries
          if (typeof shared === 'object') {
            return shared.userId || shared.id;
          }
          return shared;
        }).filter(Boolean);
        
        setSharedWith(sharedUserIds);
      }
    }
  }, [isOpen, initialEvent, isEditMode]);

  // Get connections for the selected type
  const getConnectionsForType = (type) => {
    if (!connections) return [];
    
    switch(type) {
      case 'family':
        return connections.family || [];
      case 'friend':
        return connections.friends || [];
      case 'other':
        return connections.business || [];
      case 'all':
        return [
          ...(connections.family || []),
          ...(connections.friends || []),
          ...(connections.business || [])
        ];
      default:
        return [];
    }
  };

  // Get the filtered connections based on the selected type
  const filteredConnections = getConnectionsForType(connectionType);

  // Add a useEffect hook to log connections details when component mounts
  useEffect(() => {
    // If we have family connections, log them for detailed inspection
    if (connections && connections.family) {
      // Log the first family connection's structure if available
      if (connections.family.length > 0) {
        // Family connection ID
        const connectionId = connections.family[0]._id || 
                           connections.family[0].id || 
                           connections.family[0].connectedUserId;
      }
    }
  }, [connections]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user?.id) {
      setError('User authentication required');
      return;
    }
    
    try {
      setIsSubmitting(true);
      setError(null);
      
      // Calculate end time
      const startTime = new Date(startDateTime);
      const endTime = addMinutes(startTime, duration.hours * 60 + duration.minutes);
      
      // Transform sharedWith from array of IDs to array of objects with userId property
      const formattedSharedWith = sharedWith.map(userId => ({ userId }));
      
      // Prepare event data with proper field names required by the API
      const eventData = {
        title: title,
        description: description || '',
        location: location || '',
        // Use startTime and endTime fields as required by the API
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
        // Also include start and end for compatibility
        start: startTime.toISOString(),
        end: endTime.toISOString(),
        // Use userId field as required by the API
        userId: user.id,
        duration: duration.hours * 60 + duration.minutes,
        sharedWith: formattedSharedWith,
        color: color || '#3B82F6',
      };
      
      let result;
      
      if (isEditMode && initialEvent?.id) {
        // Update existing event
        eventData.id = initialEvent.id; // Add ID for update
        result = await calendarService.updateEvent(eventData);
        console.log('Event updated:', result);
      } else {
        // Create new event
        result = await calendarService.createEvent(eventData);
        console.log('Event created:', result);
      }
      
      // Reset form after successful operation
      setTitle('');
      setDescription('');
      setLocation('');
      setStartDateTime('');
      setDuration({ hours: 1, minutes: 0 });
      setColor('#3B82F6');
      setSharedWith([]);
      
      // Trigger refresh of events in parent component
      if (onEventAdded) {
        onEventAdded(result);
      }
      
      // Show success message and close form if in modal
      setSuccessMessage(isEditMode ? 'Event updated successfully' : 'Event created successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
      
      if (onClose) {
        onClose();
      }
    } catch (error) {
      setError(error.message || `Failed to ${isEditMode ? 'update' : 'create'} event. Please try again.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDurationChange = (type, value) => {
    const newDuration = {
      ...duration,
      [type]: parseInt(value) || 0
    };
    setDuration(newDuration);

    // Calculate new end time based on start time and duration
    if (startDateTime) {
      const totalMinutes = (newDuration.hours * 60) + newDuration.minutes;
      const newEndTime = addMinutes(parseISO(startDateTime), totalMinutes);
    }
  };

  const toggleConnectionSelection = (connection) => {
    const connectionId = connection._id || connection.id || connection.connectedUserId;
    
    setSharedWith(prev => 
      prev.includes(connectionId)
        ? prev.filter(id => id !== connectionId)
        : [...prev, connectionId]
    );
  };

  const isConnectionSelected = (connection) => {
    const connectionId = connection._id || connection.id || connection.connectedUserId;
    return sharedWith.includes(connectionId);
  };

  // On the modal close button
  const handleClose = () => {
    // Reset form state
    setTitle('');
    setDescription('');
    setStartDateTime('');
    setDuration({ hours: 1, minutes: 0 });
    setLocation('');
    setColor('#3B82F6');
    setSharedWith([]);
    setIsShareOpen(false);
    // Close the form
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-pri-dark/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 sm:p-6"
          onClick={handleClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", damping: 25 }}
            className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto hide-scrollbar"
            onClick={e => e.stopPropagation()}
          >
            {/* Glossy Card */}
            <div className="relative bg-gradient-to-b from-sec-dark/90 to-pri-dark/90 rounded-2xl shadow-2xl overflow-hidden">
              {/* Glossy Effects */}
              <div className="absolute inset-0 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-soft-pink/10 via-transparent to-mint-green/5" />
                <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-pri-white/10 to-transparent" />
                <div className="absolute -left-24 -top-24 w-48 h-48 bg-soft-pink/20 rounded-full filter blur-[80px] opacity-30" />
                <div className="absolute -right-24 -bottom-24 w-48 h-48 bg-mint-green/20 rounded-full filter blur-[80px] opacity-20" />
                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-soft-pink/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
              </div>

              {/* Form Content */}
              <div className="relative p-5 sm:p-6">
                {/* Header with Title and Close Button */}
                <div className="flex justify-between items-center mb-6">
                  <motion.h2 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-soft-pink to-mint-green bg-clip-text text-transparent"
                  >
                    {isEditMode ? 'Edit Event' : 'Create New Event'}
                  </motion.h2>
                  <motion.button
                    whileHover={{ scale: 1.1, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={handleClose}
                    className="p-1 rounded-full bg-pri-dark/40 hover:bg-pri-dark/60 text-pri-white transition-all"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </motion.button>
                </div>

                {/* Success Message */}
                <AnimatePresence>
                  {successMessage && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="mb-4 p-3 rounded-lg bg-gradient-to-r from-mint-green/20 to-mint-green/10 text-mint-green border border-mint-green/20"
                    >
                      <div className="flex items-center">
                        <FaCheck className="h-4 w-4 mr-2" />
                        <span>{successMessage}</span>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Error Message */}
                <AnimatePresence>
                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="mb-4 p-3 rounded-lg bg-gradient-to-r from-red-500/20 to-red-500/10 text-red-500 border border-red-500/20"
                    >
                      <div className="flex items-center">
                        <span className="text-sm">{error}</span>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Event Title */}
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Add title"
                      required
                      className="w-full bg-gradient-to-r from-pri-dark/60 to-sec-dark/60 border border-soft-pink/20 text-pri-white placeholder-pri-gray rounded-xl px-4 py-3 focus:outline-none focus:border-soft-pink/50 focus:ring-1 focus:ring-soft-pink/30 transition-all duration-300"
                    />
                  </div>

                  {/* Time Section - Combined Start Time and Duration */}
                  <motion.div 
                    className="space-y-2"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                  >
                    <div className="flex items-center gap-2 text-soft-pink mb-1 text-sm">
                      <ClockIcon className="h-4 w-4" />
                      <span>Time & Duration</span>
                    </div>
                    <div className="flex gap-3">
                      {/* Start Time */}
                      <div className="flex-1">
                        <input
                          type="datetime-local"
                          value={startDateTime}
                          onChange={(e) => setStartDateTime(e.target.value)}
                          required
                          className="w-full bg-gradient-to-r from-pri-dark/60 to-sec-dark/60 border border-soft-pink/20 text-pri-white rounded-xl px-4 py-3 focus:outline-none focus:border-soft-pink/50 focus:ring-1 focus:ring-soft-pink/30 transition-all duration-300"
                        />
                      </div>

                      {/* Duration Dropdowns */}
                      <div className="flex gap-2 flex-1">
                        <select
                          value={duration.hours}
                          onChange={(e) => handleDurationChange('hours', e.target.value)}
                          className="w-1/2 bg-pri-dark border border-soft-pink/20 text-pri-white rounded-xl px-3 py-3 focus:outline-none focus:border-soft-pink/50 focus:ring-1 focus:ring-soft-pink/30 transition-all duration-300 appearance-none"
                          style={{ 
                            WebkitAppearance: 'none', 
                            MozAppearance: 'none',
                            backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%23ffffff\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")',
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'right 8px center',
                            backgroundSize: '16px',
                            paddingRight: '32px'
                          }}
                        >
                          {Array.from({ length: 24 }, (_, i) => (
                            <option key={i} value={i} style={{ backgroundColor: '#1a1c2e', color: 'white' }}>{i} hr{i !== 1 ? 's' : ''}</option>
                          ))}
                        </select>
                        <select
                          value={duration.minutes}
                          onChange={(e) => handleDurationChange('minutes', e.target.value)}
                          className="w-1/2 bg-pri-dark border border-soft-pink/20 text-pri-white rounded-xl px-3 py-3 focus:outline-none focus:border-soft-pink/50 focus:ring-1 focus:ring-soft-pink/30 transition-all duration-300 appearance-none"
                          style={{ 
                            WebkitAppearance: 'none', 
                            MozAppearance: 'none',
                            backgroundImage: 'url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%23ffffff\' stroke-width=\'2\' stroke-linecap=\'round\' stroke-linejoin=\'round\'%3e%3cpolyline points=\'6 9 12 15 18 9\'%3e%3c/polyline%3e%3c/svg%3e")',
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'right 8px center',
                            backgroundSize: '16px',
                            paddingRight: '32px'
                          }}
                        >
                          {[0, 15, 30, 45].map(min => (
                            <option key={min} value={min} style={{ backgroundColor: '#1a1c2e', color: 'white' }}>{min} min</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    
                    {/* End Time Display */}
                    {startDateTime && (
                      <div className="mt-2 px-4 py-2 bg-gradient-to-r from-pri-dark/40 to-sec-dark/40 rounded-xl border border-soft-pink/10">
                        <span className="text-sm text-soft-pink">
                          Ends at: {format(addMinutes(parseISO(startDateTime), duration.hours * 60 + duration.minutes), "MMM d, yyyy h:mm a")}
                        </span>
                      </div>
                    )}
                  </motion.div>

                  {/* Location */}
                  <motion.div 
                    className="space-y-2"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                  >
                    <div className="flex items-center gap-2 text-soft-pink mb-1 text-sm">
                      <MapPinIcon className="h-4 w-4" />
                      <span>Location</span>
                    </div>
                    <input
                      type="text"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      placeholder="Add location"
                      className="w-full bg-gradient-to-r from-pri-dark/60 to-sec-dark/60 border border-soft-pink/20 text-pri-white placeholder-pri-gray rounded-xl px-4 py-3 focus:outline-none focus:border-soft-pink/50 focus:ring-1 focus:ring-soft-pink/30 transition-all duration-300"
                    />
                  </motion.div>

                  {/* Description */}
                  <motion.div 
                    className="space-y-2"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                  >
                    <div className="flex items-center gap-2 text-soft-pink mb-1 text-sm">
                      <span>Description</span>
                    </div>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Add description"
                      rows={3}
                      className="w-full bg-gradient-to-r from-pri-dark/60 to-sec-dark/60 border border-soft-pink/20 text-pri-white placeholder-pri-gray rounded-xl px-4 py-3 focus:outline-none focus:border-soft-pink/50 focus:ring-1 focus:ring-soft-pink/30 transition-all duration-300 resize-none"
                    />
                  </motion.div>

                  {/* Color */}
                  <motion.div 
                    className="space-y-2"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                  >
                    <div className="flex items-center gap-2 text-soft-pink mb-1 text-sm">
                      <SwatchIcon className="h-4 w-4" />
                      <span>Color</span>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {['#F87171', '#60A5FA', '#34D399', '#A78BFA', '#FBBF24', '#EC4899', '#6EE7B7', '#93C5FD'].map(colorOption => (
                        <motion.button
                          key={colorOption}
                          type="button"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => setColor(colorOption)}
                          className={`w-8 h-8 rounded-full transition-transform ${
                            color === colorOption ? 'ring-2 ring-offset-2 ring-offset-pri-dark ring-pri-white scale-110' : ''
                          }`}
                          style={{ backgroundColor: colorOption }}
                        />
                      ))}
                    </div>
                  </motion.div>

                  {/* Share with Connections */}
                  <motion.div 
                    className="space-y-2 pt-2"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-soft-pink mb-1 text-sm">
                        <FaShareAlt className="h-4 w-4" />
                        <span>Share with</span>
                      </div>
                      <motion.button
                        type="button"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setIsShareOpen(!isShareOpen)}
                        className="flex items-center gap-1 text-sm text-pri-white/80 hover:text-soft-pink transition-colors duration-200"
                      >
                        {isShareOpen ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
                        <span>{isShareOpen ? 'Collapse' : 'Expand'}</span>
                      </motion.button>
                    </div>

                    <AnimatePresence>
                      {isShareOpen && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          {/* Connection Types */}
                          <div className="flex gap-2 mb-3 overflow-x-auto pb-1 scrollbar-hide">
                            {['family', 'friend', 'other', 'all'].map(type => (
                              <motion.button
                                key={type}
                                type="button"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => setConnectionType(type)}
                                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 border ${
                                  connectionType === type
                                    ? 'bg-soft-pink/20 text-soft-pink border-soft-pink/30'
                                    : 'bg-pri-dark/40 text-pri-white/70 border-pri-white/10 hover:bg-pri-dark/60'
                                }`}
                              >
                                {type.charAt(0).toUpperCase() + type.slice(1)}
                              </motion.button>
                            ))}
                          </div>

                          {/* Connections List */}
                          <div className="bg-pri-dark/30 rounded-xl border border-soft-pink/10 max-h-40 overflow-y-auto p-2 space-y-1">
                            {isLoading ? (
                              <div className="flex justify-center items-center py-4">
                                <motion.div
                                  animate={{ rotate: 360 }}
                                  transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                                  className="h-5 w-5 border-2 border-soft-pink/30 border-t-soft-pink rounded-full"
                                />
                              </div>
                            ) : filteredConnections.length === 0 ? (
                              <div className="text-center py-3 text-pri-gray text-sm">
                                No connections found
                              </div>
                            ) : (
                              filteredConnections.map(connection => (
                                <motion.div
                                  key={connection._id || connection.id || connection.connectedUserId}
                                  whileHover={{ scale: 1.02, x: 3 }}
                                  className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-all duration-200 ${
                                    isConnectionSelected(connection)
                                      ? 'bg-soft-pink/20 border border-soft-pink/30'
                                      : 'bg-pri-dark/40 border border-pri-white/5 hover:bg-pri-dark/60'
                                  }`}
                                  onClick={() => toggleConnectionSelection(connection)}
                                >
                                  <div className="flex items-center gap-2">
                                    {connection.avatar ? (
                                      <img 
                                        src={connection.avatar} 
                                        alt={connection.name || 'User'} 
                                        className="h-6 w-6 rounded-full object-cover"
                                      />
                                    ) : (
                                      <div className="h-6 w-6 rounded-full bg-gradient-to-br from-soft-pink/30 to-mint-green/30 flex items-center justify-center">
                                        <FaUserFriends className="h-3 w-3 text-pri-white" />
                                      </div>
                                    )}
                                    <span className="text-sm text-pri-white truncate">
                                      {connection.name || connection.userName || 'User'}
                                    </span>
                                  </div>
                                  <div className={`h-4 w-4 rounded-full border ${
                                    isConnectionSelected(connection)
                                      ? 'bg-soft-pink border-soft-pink/70'
                                      : 'border-pri-white/30'
                                  }`}>
                                    {isConnectionSelected(connection) && (
                                      <motion.div
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        className="flex items-center justify-center h-full w-full"
                                      >
                                        <span className="text-pri-dark text-[8px]">✓</span>
                                      </motion.div>
                                    )}
                                  </div>
                                </motion.div>
                              ))
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>

                  {/* Submit Button */}
                  <motion.div 
                    className="flex justify-end gap-3 pt-3"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7 }}
                  >
                    <motion.button
                      type="button"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={handleClose}
                      className="px-5 py-2.5 rounded-xl bg-pri-dark/60 text-pri-white hover:bg-pri-dark transition-colors"
                      disabled={isSubmitting}
                    >
                      Cancel
                    </motion.button>
                    <motion.button
                      type="submit"
                      whileHover={{ scale: 1.05, boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)' }}
                      whileTap={{ scale: 0.95 }}
                      className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-soft-pink to-mint-green text-pri-dark font-medium shadow-lg disabled:opacity-70 disabled:cursor-not-allowed"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <div className="flex items-center gap-2">
                          <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                            className="h-4 w-4 border-2 border-pri-dark/20 border-t-pri-dark rounded-full"
                          />
                          <span>{isEditMode ? 'Updating...' : 'Creating...'}</span>
                        </div>
                      ) : (
                        isEditMode ? 'Update Event' : 'Create Event'
                      )}
                    </motion.button>
                  </motion.div>
                </form>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
